import React, { useEffect, useMemo, useState } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import { Box, Button } from '@mui/material';
import axios from '../utils/axios';

export default function BugTracker() {
  const theme = useTheme();
  const { palette } = theme;
  const panelStyle = { background: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 12, padding: 20 };
  const fieldStyle = { padding: '12px 14px', minHeight: 44, boxSizing: 'border-box', borderRadius: 8, border: `1px solid ${palette.divider}`, background: palette.background.default, color: palette.text.primary, font: 'inherit', colorScheme: palette.mode };
  const cellStyle = { padding: '14px 16px', textAlign: 'left', verticalAlign: 'top', overflowWrap: 'anywhere' };
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const mapStatus = (s) => {
    if (s === 'done') return 'исполнен';
    if (s === 'in_progress') return 'в работе';
    return s || '';
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, status]);

  const fetchList = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/bugtr/my', { params: { page, limit, status: status || undefined } });
      setItems(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(e.response?.data?.error || 'Ошибка получения списка');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setError('');
    setSuccess('');
    if (!name.trim()) {
      setError('name обязателен');
      return;
    }
    try {
      const res = await axios.post('/bugtr', { name: name.trim(), description: description.trim() });
      setSuccess('Тикет создан');
      setName('');
      setDescription('');
      // refresh list
      setPage(1);
      await fetchList();
    } catch (e) {
      setError(e.response?.data?.error || 'Ошибка создания (server error)');
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, md: 3 }, color: 'text.primary', '& input::placeholder, & textarea::placeholder': { color: 'text.secondary', opacity: 1 }, '& input:focus-visible, & textarea:focus-visible, & select:focus-visible': { outline: `2px solid ${palette.primary.main}`, outlineOffset: 2 }, '& tbody tr:hover': { backgroundColor: alpha(palette.primary.main, 0.04) }, '& .MuiButton-root': { minHeight: 44 } }}>
      <h2 style={{ marginTop: 0, marginBottom: 24 }}>Баг-трекер мерчанта</h2>

      {/* Информационное окно */}
      <div style={{ background: alpha(palette.secondary.main, 0.08), border: `1px solid ${alpha(palette.secondary.main, 0.3)}`, color: palette.text.primary, borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>О сервисе</div>
        <div style={{ lineHeight: 1.45 }}>
          Тут вы можете создавать тикеты с описанием ошибок или пожеланий по панели. 
          После создания тикета он появится в списке ниже. Статусы:
          <ul style={{ margin: '6px 0 0 18px' }}>
            <li><b>в работе</b> — тикет принят и обрабатывается (in_progress)</li>
            <li><b>исполнен</b> — задача выполнена (done)</li>
          </ul>
        </div>
      </div>

      <div style={{ ...panelStyle, marginBottom: 20 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Создать тикет</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            type="text"
            placeholder="Краткое название (name)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Краткое название"
            style={fieldStyle}
          />
          <textarea
            placeholder="Описание (description)"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            aria-label="Описание"
            style={{ ...fieldStyle, resize: 'vertical', minHeight: 110 }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handleCreate}>Создать</Button>
          </div>
        </div>
      </div>

      <div style={panelStyle}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 20 }}>
          <label htmlFor="bug-status" style={{ fontWeight: 600 }}>Фильтр:</label>
          <select id="bug-status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={fieldStyle}>
            <option value="">Все</option>
            <option value="in_progress">в работе</option>
            <option value="done">исполнен</option>
          </select>
          <div style={{ marginLeft: 'auto' }}>
            <label htmlFor="bug-limit" style={{ marginRight: 8, color: palette.text.secondary }}>Лимит</label>
            <select id="bug-limit" value={limit} onChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(1); }} style={fieldStyle}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div>Загрузка...</div>
        ) : (
          <>
            <div style={{ marginBottom: 12, color: palette.text.secondary }}>Всего: <b>{total}</b></div>
            <div style={{ overflowX: 'auto', border: `1px solid ${palette.divider}`, borderRadius: 8 }}>
            <table style={{ width: '100%', minWidth: 600, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ background: palette.action.hover, color: palette.text.secondary }}>
                  <th style={cellStyle}>Название</th>
                  <th style={cellStyle}>Описание</th>
                  <th style={cellStyle}>Статус</th>
                  <th style={cellStyle}>Ответ</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: 24, textAlign: 'center', color: palette.text.secondary }}>Записей нет</td>
                  </tr>
                ) : (
                  items.map((t) => (
                    <tr key={t._id} style={{ borderBottom: `1px solid ${palette.divider}` }}>
                      <td style={cellStyle}>{t.name}</td>
                      <td style={{ ...cellStyle, whiteSpace: 'pre-wrap' }}>{t.description}</td>
                      <td style={cellStyle}>
                        <span style={{
                          display: 'inline-block', padding: '3px 8px', borderRadius: 6,
                          background: alpha(t.status === 'done' ? palette.success.main : palette.warning.main, 0.1),
                          color: t.status === 'done' ? palette.success.main : palette.warning.main,
                          border: `1px solid ${alpha(t.status === 'done' ? palette.success.main : palette.warning.main, 0.25)}`,
                          fontSize: 12,
                        }}>
                          {mapStatus(t.status)}
                        </span>
                      </td>
                      <td style={cellStyle}>{t.ask}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <Button variant="outlined" aria-label="Предыдущая страница" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>{'<'}</Button>
              <span>Стр. {page} из {totalPages}</span>
              <Button variant="outlined" aria-label="Следующая страница" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>{'>'}</Button>
            </div>
          </>
        )}

        {error && (
          <div role="alert" style={{ marginTop: 12, color: palette.error.main, background: alpha(palette.error.main, 0.08), padding: '12px 16px', borderRadius: 8 }}>{error}</div>
        )}
        {success && (
          <div role="status" style={{ marginTop: 12, color: palette.success.main, background: alpha(palette.success.main, 0.08), padding: '12px 16px', borderRadius: 8 }}>{success}</div>
        )}
      </div>
    </Box>
  );
}
